import NProgress from 'nprogress'
import { createApp, h } from 'vue'
import { createInertiaApp, router } from '@inertiajs/vue3'
import MainCompanyLayout from '@/Layouts/Company/MainCompanyLayout.vue'
import MainCustomerLayout from '@/Layouts/Customer/MainCustomerLayout.vue'
import Link from '@/Components/Shared/Link/Link.js'
import * as Sentry from '@sentry/vue'
import Sortable, { MultiDrag } from 'sortablejs'
import { resolvePageComponent } from 'laravel-vite-plugin/inertia-helpers'
import { useRunwaySlideoverBodyAttribute } from '@/Hooks/Shared/useRunwaySlideoverBodyAttribute.js'
import {
  initDatadogRum,
  getViewNameFromPage,
  startRumView,
  stopRumSession,
} from '@/Utils/DatadogRum.js'

import '../css/v2.css'
import 'intl-tel-input/build/css/intlTelInput.css'

// Mount this here so that it only happens once
Sortable.mount(new MultiDrag())

// Define UploadCare translations accessible to `window`
window.UPLOADCARE_LOCALE_TRANSLATIONS = {
  errors: {
    fileMaximumSize: 'File is too big, the maximum file size is 100MB.',
    imageMaximumResolution: 'Image is too large, maximum total pixels allowed is 178 Megapixels.',
    imageMinimumResolution: 'Image is too small, the minimum image size is 800px by 600px.',
    videoFileType: 'This file does not appear to be a video.',
  },
}

const globalHelpers = {
  install: (app) => {
    app.mixin({
      computed: {
        pageURL() {
          return this.$page.url
        },
      },
      methods: {
        route: window.route,
        isPath(url) {
          return url === `${window.location.origin}${window.location.pathname}`
        },
      },
    })
  },
}

createInertiaApp({
  resolve: (name) =>
    resolvePageComponent(`./Pages/${name}.vue`, import.meta.glob('./Pages/**/*.vue')).then(
      (module) => {
        if (module.default.layout === undefined) {
          let layout = null
          switch (name.split('/')[0]) {
            case 'Company':
              layout = MainCompanyLayout
              break
            case 'Customer':
              layout = MainCustomerLayout
              break
            default:
              break
          }

          module.default.layout = layout
        }

        return module
      }
    ),
  progress: false,
  setup({ el, App, props, plugin }) {
    initDatadogRum()

    try {
      const appEl = document.getElementById('app')
      const pageData = appEl?.getAttribute('data-page')
      const initialPage = pageData ? JSON.parse(pageData) : null

      startRumView(getViewNameFromPage(initialPage))
    } catch {
      startRumView(window.location.pathname)
    }

    const vueApp = createApp({ render: () => h(App, props) })
      .use(plugin)
      .use(globalHelpers)
      .component('aryeo-link', Link)

    if (window.ARYEO_SENTRY_DSN) {
      Sentry.init({
        environment: window.ARYEO_ENVIRONMENT,
        app: vueApp,
        dsn: window.ARYEO_SENTRY_DSN,
        denyUrls: [
          // Chrome extensions
          /extensions\//i,
          /^chrome:\/\//i,
          /^chrome-extension:\/\//i,
          // Facebook flakiness
          /graph\.facebook\.com/i,
          // Google Analytics and Google Tag Manager
          /\/(gtm|ga|analytics)\.js/i,
          // Facebook blocked
          /connect\.facebook\.net\/en_US\/all\.js/i,
          // Firefox Extensions
          /^resource:\/\//i,
          // Safari Extensions
          /.*@webkit-masked-url.*/,
          // Cookie Consent Banners via GTM
          /otSDKStub.js/i,
          /cookielaw.org/i,
        ],
        integrations: [
          new Sentry.BrowserTracing({
            tracePropagationTargets: [window.ARYEO_COMPANY_API_V1_BASE_URL],
            routingInstrumentation: function (
              startTransaction,
              startTransactionOnPageLoad,
              startTransactionOnLocationChange
            ) {
              const tags = {
                'routing.instrumentation': 'inertia',
              }
              if (startTransactionOnPageLoad) {
                startTransaction({
                  name: window.location.pathname,
                  op: 'pageload',
                  tags,
                  metadata: {
                    source: 'url',
                  },
                })
              }

              if (startTransactionOnLocationChange) {
                router.on('start', function (event) {
                  if (event.detail.visit.prefetch) {
                    return
                  }

                  startTransaction({
                    name: event.detail.visit.url.pathname,
                    op: 'navigation',
                    tags,
                    data: {
                      from: window.location.pathname,
                      to: event.detail.visit.url.pathname,
                      query: event.detail.visit.url.search,
                    },
                  })
                })
              }
            },
          }),
          new Sentry.Replay({
            maskAllText: false,
            maskAllInputs: true,
          }),
        ],
        // Performance Monitoring
        tracesSampleRate: 0, // Percentage of transactions to be sent to Sentry. Default is 0.1 (10%)
        // Session Replay
        replaysSessionSampleRate: 0.1, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
        replaysOnErrorSampleRate: 1.0, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
      })
    }

    let timeout = null

    router.on('start', (event) => {
      if (event.detail.visit.prefetch) {
        return
      }

      timeout = setTimeout(() => NProgress.start(), 250)
    })

    router.on('progress', (event) => {
      if (event.detail.visit.prefetch) {
        return
      }

      if (NProgress.isStarted() && event.detail.progress.percentage) {
        NProgress.set((event.detail.progress.percentage / 100) * 0.9)
      }
    })

    router.on('finish', (event) => {
      if (event.detail.visit.prefetch) {
        return
      }

      clearTimeout(timeout)

      if (event.detail.visit.completed) {
        NProgress.done()
      } else if (event.detail.visit.interrupted) {
        NProgress.set(0)
      } else if (event.detail.visit.cancelled) {
        NProgress.done()
        NProgress.remove()
      }
    })

    useRunwaySlideoverBodyAttribute()
    router.on('navigate', (event) => {
      const viewName = getViewNameFromPage(event.detail.page)

      if (viewName.includes('ShowLogin')) {
        stopRumSession()
      }

      startRumView(viewName)
    })

    vueApp.mount(el)
  },
})
